import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { RatingBadge } from '../../../../shared/RatingBadge.js';
import Stepper from '../../../../shared/Stepper.js';
import { simulateFlowReductionSavings, DEFAULT_REBOUND_RATE } from '../../calculations/flowReductionCalculations.js';
import { formatPaybackYears as formatPayback } from '../../calculations/profitability.js';

const FLOW_SHORTCUTS = [3, 4, 6];

const STEP_LABELS = [
  { label: 'Composition' },
  { label: 'Rebond' },
  { label: 'Résultats' },
];

/**
 * Generic 3-step wizard: composition (build one or more "lots" — a chosen product applied to a
 * set of equipment, repeatable) → rebound rate → results. Shared by the aerator (faucets) and
 * shower flow-reducer sub-modules — mechanically identical, differing only in equipment type,
 * optional subtype grouping/quick-select, product catalog, and copy.
 *
 * A lot is `{ id, modelId: string|null, manualFlow: number|null, equipmentIds: Set<string> }`.
 * `modelId` references an entry in `catalog`; when null, `manualFlow` is used instead (fallback
 * for when no catalog entry fits). Each committed equipment can only belong to one lot at a time.
 *
 * @param {'faucet'|'shower'} equipmentType
 * @param {string} equipmentTypeLabel - e.g. "mousseur", "réducteur de débit"
 * @param {string} targetLabel - e.g. "robinet", "douche" (singular, used in table/quick-select copy)
 * @param {string} targetLabelPlural - e.g. "robinets", "douches"
 * @param {string} moduleEyebrow - e.g. "Mousseurs", "Douches"
 * @param {boolean} hasSubtypeGrouping - true for faucets (groups by technical_data.faucet_type)
 * @param {string|null} subtypeField - technical_data key to group by, only used when hasSubtypeGrouping
 * @param {Object|null} subtypeLabels - subtypeField value -> display label
 * @param {string[]|null} subtypeOrder - display order of subtype values
 * @param {Object[]} catalog - product catalog: { id, brand, model, flowRateLMin, compatibleFaucetTypes, technology, notes }
 * @param {Object[]} equipments - full unfiltered equipment list; filtered internally by equipmentType
 * @param {Object[]} usages
 * @param {Object|null} waterPricing - { waterPrice, electricityPrice, fuelPrice, waterHeatingType, hotWaterSetpoint, waterHeatingEfficiency }
 * @param {(result: Object) => void} onResultChange - reports the simulation result up to the parent
 */
const FlowReductionWizard = ({
  equipmentType,
  equipmentTypeLabel,
  targetLabel,
  targetLabelPlural,
  moduleEyebrow,
  hasSubtypeGrouping,
  subtypeField,
  subtypeLabels,
  subtypeOrder,
  catalog,
  equipments,
  usages,
  waterPricing,
  onResultChange,
}) => {
  const [lots, setLots] = useState([]);
  const [editingLotId, setEditingLotId] = useState(null);
  const [draftIsManual, setDraftIsManual] = useState(false);
  const [draftModelId, setDraftModelId] = useState('');
  const [draftManualFlow, setDraftManualFlow] = useState('');
  const [draftEquipmentIds, setDraftEquipmentIds] = useState(new Set());
  const [expandedModelId, setExpandedModelId] = useState(null);
  const [reboundRate, setReboundRate] = useState(DEFAULT_REBOUND_RATE);

  const [currentStep, setCurrentStep] = useState(0); // 0=composition, 1=rebond, 2=résultats
  const [furthestStepReached, setFurthestStepReached] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sectionFilter, setSectionFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);

  const eligibleEquipments = useMemo(
    () => equipments.filter(e => e.type_equipment === equipmentType),
    [equipments, equipmentType]
  );

  const eligibleEquipmentsById = useMemo(
    () => new Map(eligibleEquipments.map(e => [e._id, e])),
    [eligibleEquipments]
  );

  // Un point d'eau sélectionné peut représenter plusieurs unités physiques : plusieurs
  // équipements identiques dans une pièce (technical_data.nb_equipment) et/ou une pièce
  // répétée à l'identique (location.room_multiplier, cf. nb_identical_rooms).
  const countPhysicalUnits = useCallback((equipmentIds) => {
    let sum = 0;
    equipmentIds.forEach(id => {
      const eq = eligibleEquipmentsById.get(id);
      const nbEquipment = eq?.technical_data?.nb_equipment ?? 1;
      const roomMultiplier = eq?.location?.room_multiplier ?? 1;
      sum += nbEquipment * roomMultiplier;
    });
    return sum;
  }, [eligibleEquipmentsById]);

  const getSubtype = useCallback(
    (e) => (hasSubtypeGrouping ? (e.technical_data?.[subtypeField] || 'other') : null),
    [hasSubtypeGrouping, subtypeField]
  );

  /* ── Lot bookkeeping: an equipment already committed to another lot leaves the pool.
     Editing a lot puts its own equipment back in the pool so it can be re-picked. ── */
  const assignedElsewhere = useMemo(() => {
    const ids = new Set();
    lots.forEach(lot => {
      if (lot.id === editingLotId) return;
      lot.equipmentIds.forEach(id => ids.add(id));
    });
    return ids;
  }, [lots, editingLotId]);

  const poolEquipments = useMemo(
    () => eligibleEquipments.filter(e => !assignedElsewhere.has(e._id)),
    [eligibleEquipments, assignedElsewhere]
  );

  const sections = useMemo(() => Array.from(
    new Map(poolEquipments.map(e => [e.location?.section_id, e.location?.name_section || e.location?.section_id])).entries()
  ).map(([value, label]) => ({ value, label })), [poolEquipments]);

  const visibleEquipments = useMemo(
    () => poolEquipments.filter(e =>
      (sectionFilter.length === 0 || sectionFilter.includes(e.location?.section_id)) &&
      (!hasSubtypeGrouping || typeFilter.length === 0 || typeFilter.includes(getSubtype(e)))
    ),
    [poolEquipments, sectionFilter, typeFilter, hasSubtypeGrouping, getSubtype]
  );

  const subtypeCounts = useMemo(() => {
    if (!hasSubtypeGrouping) return {};
    const counts = {};
    subtypeOrder.forEach(type => {
      counts[type] = poolEquipments.filter(e => getSubtype(e) === type).length;
    });
    return counts;
  }, [poolEquipments, hasSubtypeGrouping, subtypeOrder, getSubtype]);

  /* ── Draft lot (in-progress, not yet committed to `lots`) ── */
  const draftModel = !draftIsManual ? catalog.find(m => m.id === draftModelId) ?? null : null;
  const parsedManualFlow = draftManualFlow === '' ? null : parseFloat(draftManualFlow);
  const draftFlow = draftIsManual ? parsedManualFlow : (draftModel?.flowRateLMin ?? null);

  const canCommitLot = draftFlow != null && draftFlow > 0 && draftEquipmentIds.size > 0;

  const resetDraft = () => {
    setEditingLotId(null);
    setDraftIsManual(false);
    setDraftModelId('');
    setDraftManualFlow('');
    setDraftEquipmentIds(new Set());
    setExpandedModelId(null);
  };

  // Catalog entries carry more data than fits on the card face — this drives the
  // "info" popover, skipping any field a given entry doesn't have.
  const modelDetailRows = (model) => {
    const rows = [];
    if (model.reference) rows.push(['Référence', model.reference]);
    if (model.family) rows.push(['Gamme', model.family]);
    if (model.technology) rows.push(['Technologie', model.technology]);
    const connection = [model.connectionType, model.connectionSize].filter(Boolean).join(' · ');
    const connectionLine = model.pressureRangeBar
      ? `${connection ? connection + ' · ' : ''}pression ${model.pressureRangeBar} bar`
      : connection;
    if (connectionLine) rows.push(['Raccordement', connectionLine]);
    if (model.pressureCompensated != null) rows.push(['Pression compensée', model.pressureCompensated ? 'Oui' : 'Non']);
    if (model.temporaryBoostFlowRateLMin != null) rows.push(['Débit de secours', `${model.temporaryBoostFlowRateLMin} L/min`]);
    if (model.replacementFlowRateLMin != null) rows.push(['Débit typique remplacé', `${model.replacementFlowRateLMin} L/min`]);
    if (model.recommendedApplications?.length) rows.push(['Applications', model.recommendedApplications.join(', ')]);
    return rows;
  };

  const commitLot = () => {
    if (!canCommitLot) return;
    const lot = {
      id: editingLotId || uuidv4(),
      modelId: draftIsManual ? null : draftModelId,
      manualFlow: draftIsManual ? draftFlow : null,
      equipmentIds: new Set(draftEquipmentIds),
    };
    setLots(prev => editingLotId ? prev.map(l => (l.id === editingLotId ? lot : l)) : [...prev, lot]);
    resetDraft();
  };

  const startEditLot = (lot) => {
    setEditingLotId(lot.id);
    setDraftIsManual(lot.modelId == null);
    setDraftModelId(lot.modelId || '');
    setDraftManualFlow(lot.manualFlow != null ? String(lot.manualFlow) : '');
    setDraftEquipmentIds(new Set(lot.equipmentIds));
  };

  const deleteLot = (lotId) => {
    setLots(prev => prev.filter(l => l.id !== lotId));
    if (editingLotId === lotId) resetDraft();
  };

  const getLotFlow = (lot) => lot.manualFlow ?? catalog.find(m => m.id === lot.modelId)?.flowRateLMin ?? null;
  const getLotLabel = (lot) => {
    if (lot.modelId == null) return 'Débit manuel';
    const model = catalog.find(m => m.id === lot.modelId);
    return model ? `${model.brand} ${model.model}` : 'Modèle inconnu';
  };

  /* ── Simulation: union of every lot's equipment, one target flow per equipment via `overrides` ── */
  const overrides = useMemo(() => {
    const map = new Map();
    lots.forEach(lot => {
      const flow = getLotFlow(lot);
      lot.equipmentIds.forEach(id => map.set(id, flow));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lots, catalog]);

  // A manual-flow lot (no catalog modelId) has no known unit price — its equipment maps to
  // null, which makes investmentFullyKnown false for the whole simulation.
  const priceOverrides = useMemo(() => {
    const map = new Map();
    lots.forEach(lot => {
      const price = lot.modelId != null ? catalog.find(m => m.id === lot.modelId)?.priceEur ?? null : null;
      lot.equipmentIds.forEach(id => map.set(id, price));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lots, catalog]);

  const simulatedEquipments = useMemo(
    () => eligibleEquipments.filter(e => overrides.has(e._id)),
    [eligibleEquipments, overrides]
  );

  const simulationResult = useMemo(
    () => simulateFlowReductionSavings({
      equipments: simulatedEquipments,
      usages,
      waterPricing,
      overrides,
      priceOverrides,
      reboundRate,
    }),
    [simulatedEquipments, usages, waterPricing, overrides, priceOverrides, reboundRate]
  );

  useEffect(() => {
    onResultChange?.(simulationResult);
  }, [simulationResult, onResultChange]);

  const lotBreakdown = useMemo(() => {
    if (!simulationResult.ready) return [];
    return lots.map(lot => {
      const rows = simulationResult.perEquipment.filter(r => lot.equipmentIds.has(r.equipmentId));
      const totalCurrentM3 = rows.reduce((sum, r) => sum + r.currentM3, 0);
      const totalNewM3 = rows.reduce((sum, r) => sum + r.newM3, 0);
      const totalSavingsM3 = totalCurrentM3 - totalNewM3;
      const savingsPct = totalCurrentM3 > 0 ? (totalSavingsM3 / totalCurrentM3) * 100 : 0;
      const unitPriceEur = lot.modelId != null ? catalog.find(m => m.id === lot.modelId)?.priceEur ?? null : null;
      const physicalUnits = countPhysicalUnits(lot.equipmentIds);
      const investmentEur = unitPriceEur != null ? unitPriceEur * physicalUnits : null;
      return { lot, count: physicalUnits, totalSavingsM3, savingsPct, investmentEur };
    });
  }, [lots, simulationResult, catalog, countPhysicalUnits]);

  const draftSelectedEquipments = useMemo(
    () => eligibleEquipments.filter(e => draftEquipmentIds.has(e._id)),
    [eligibleEquipments, draftEquipmentIds]
  );

  const minDraftCurrentFlow = useMemo(() => {
    if (draftSelectedEquipments.length === 0) return null;
    return Math.min(...draftSelectedEquipments.map(e => e.water_value ?? Infinity));
  }, [draftSelectedEquipments]);

  const showDraftTargetTooHighWarning = draftFlow != null && minDraftCurrentFlow != null && draftFlow >= minDraftCurrentFlow;

  /* ── Wizard navigation ── */
  const canAdvance = [
    lots.length > 0, // step 0: at least one committed lot
    true,             // step 1: rebound always has a valid default
    false,            // step 2: terminal
  ][currentStep];

  const goToStep = (index) => {
    if (index <= furthestStepReached) setCurrentStep(index);
  };

  const goNext = () => {
    if (!canAdvance || currentStep === STEP_LABELS.length - 1) return;
    const next = currentStep + 1;
    setCurrentStep(next);
    setFurthestStepReached(f => Math.max(f, next));
  };

  const goPrevious = () => {
    if (currentStep === 0) return;
    setCurrentStep(s => s - 1);
  };

  /* ── Draft equipment step: toggle all pooled equipments of a subtype ── */
  const toggleAllOfSubtype = (subtype) => {
    const groupEquipments = poolEquipments.filter(e => getSubtype(e) === subtype);
    const allSelected = groupEquipments.every(e => draftEquipmentIds.has(e._id));
    setDraftEquipmentIds(prev => {
      const next = new Set(prev);
      groupEquipments.forEach(e => (allSelected ? next.delete(e._id) : next.add(e._id)));
      return next;
    });
  };

  const toggleRow = (id) => {
    setDraftEquipmentIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allVisibleSelected = visibleEquipments.length > 0 && visibleEquipments.every(e => draftEquipmentIds.has(e._id));
  const toggleSelectAllVisible = () => {
    setDraftEquipmentIds(prev => {
      const next = new Set(prev);
      visibleEquipments.forEach(e => (allVisibleSelected ? next.delete(e._id) : next.add(e._id)));
      return next;
    });
  };

  const toggleSectionFilter = (value) => {
    setSectionFilter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const toggleTypeFilter = (value) => {
    setTypeFilter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const activeFilterCount = sectionFilter.length + typeFilter.length;

  /* ── Step render blocks ── */

  const renderCompositionStep = () => (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">{moduleEyebrow}</span>
          <h2 className="apv2-step-heading">Composition des lots</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {STEP_LABELS.length}</span>
      </div>
      <div className="apv2-step-body">
        {lots.length > 0 && (
          <div className="apv2-lot-list">
            {lots.map(lot => (
              <div className={`apv2-lot-row ${editingLotId === lot.id ? 'apv2-lot-row--editing' : ''}`} key={lot.id}>
                <div className="apv2-lot-row-main">
                  <span className="apv2-lot-row-label">{getLotLabel(lot)}</span>
                  <span className="apv2-lot-row-flow">{getLotFlow(lot)} L/min</span>
                  <span className="apv2-lot-row-count">{countPhysicalUnits(lot.equipmentIds)} {targetLabelPlural}</span>
                </div>
                <div className="apv2-lot-row-actions">
                  <button type="button" className="apv2-lot-action-btn" onClick={() => startEditLot(lot)}>Modifier</button>
                  <button type="button" className="apv2-lot-action-btn apv2-lot-action-btn--danger" onClick={() => deleteLot(lot.id)}>Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="apv2-lot-draft">
          <h3 className="apv2-lot-draft-heading">{editingLotId ? 'Modifier le lot' : `Ajouter un ${equipmentTypeLabel}`}</h3>

          <div className="apv2-step-explanation">
            <span className="apv2-step-explanation-icon">💡</span>
            <p className="apv2-step-explanation-text">
              Choisissez un {equipmentTypeLabel} du catalogue, puis sélectionnez les {targetLabelPlural} sur lesquels l'installer.
            </p>
          </div>

          <div className="apv2-model-picker">
            {catalog.map(model => {
              const isActive = !draftIsManual && draftModelId === model.id;
              const isExpanded = expandedModelId === model.id;
              const detailRows = modelDetailRows(model);
              return (
                <div className={`apv2-model-card ${isActive ? 'apv2-model-card--active' : ''}`} key={model.id}>
                  <button
                    type="button"
                    className="apv2-model-card-select"
                    onClick={() => { setDraftIsManual(false); setDraftModelId(model.id); }}
                  >
                    <div className="apv2-model-card-top">
                      {model.imageUrl ? (
                        <img className="apv2-model-card-image" src={model.imageUrl} alt="" loading="lazy" />
                      ) : (
                        <div className="apv2-model-card-image apv2-model-card-image--placeholder" aria-hidden="true" />
                      )}
                      <div className="apv2-model-card-top-text">
                        <span className="apv2-model-card-brand">{model.brand}</span>
                        <span className="apv2-model-card-name">{model.model}</span>
                      </div>
                    </div>
                    <span className="apv2-model-card-flow">{model.flowRateLMin} L/min</span>
                    {((hasSubtypeGrouping && model.compatibleFaucetTypes) || model.requiresKey) && (
                      <div className="apv2-model-card-tags">
                        {hasSubtypeGrouping && model.compatibleFaucetTypes?.map(t => (
                          <span key={t} className="apv2-model-card-tag">{subtypeLabels[t]}</span>
                        ))}
                        {model.requiresKey && (
                          <span className="apv2-model-card-tag apv2-model-card-tag--warning">Clé requise</span>
                        )}
                      </div>
                    )}
                  </button>

                  {(detailRows.length > 0 || model.notes) && (
                    <button
                      type="button"
                      className="apv2-model-card-info-btn"
                      onClick={(e) => { e.stopPropagation(); setExpandedModelId(cur => (cur === model.id ? null : model.id)); }}
                      aria-label={`Détails ${model.brand} ${model.model}`}
                      aria-expanded={isExpanded}
                    >
                      i
                    </button>
                  )}

                  {isExpanded && (
                    <div className="apv2-model-card-details">
                      {detailRows.map(([label, value]) => (
                        <div className="apv2-model-card-detail-row" key={label}>
                          <span className="apv2-model-card-detail-label">{label}</span>
                          <span className="apv2-model-card-detail-value">{value}</span>
                        </div>
                      ))}
                      {model.notes && <p className="apv2-model-card-notes">{model.notes}</p>}
                    </div>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              className={`apv2-model-card apv2-model-card--manual ${draftIsManual ? 'apv2-model-card--active' : ''}`}
              onClick={() => setDraftIsManual(true)}
            >
              <div className="apv2-model-card-brand">Débit manuel</div>
              <div className="apv2-model-card-name">Aucun modèle du catalogue ne convient</div>
            </button>
          </div>

          {draftIsManual && (
            <div className="apv2-flow-input-row">
              <label className="apv2-flow-label" htmlFor={`${equipmentType}-manual-flow`}>Débit cible (L/min)</label>
              <input
                id={`${equipmentType}-manual-flow`}
                type="number"
                min="0"
                step="0.5"
                className="apv2-flow-input"
                value={draftManualFlow}
                onChange={e => setDraftManualFlow(e.target.value)}
              />
              <div className="apv2-flow-shortcuts">
                {FLOW_SHORTCUTS.map(v => (
                  <button key={v} type="button" className="ceq-chip" onClick={() => setDraftManualFlow(String(v))}>
                    {v} L/min
                  </button>
                ))}
              </div>
            </div>
          )}

          {draftFlow != null && draftFlow > 0 && (
            <>
              {hasSubtypeGrouping && (
                <div className="apv2-quick-select-row">
                  {subtypeOrder.filter(type => subtypeCounts[type] > 0).map(type => {
                    const groupEquipments = poolEquipments.filter(e => getSubtype(e) === type);
                    const allSelected = groupEquipments.every(e => draftEquipmentIds.has(e._id));
                    return (
                      <button
                        key={type}
                        type="button"
                        className={`apv2-quick-select-btn ${allSelected ? 'apv2-quick-select-btn--active' : ''}`}
                        onClick={() => toggleAllOfSubtype(type)}
                      >
                        Tous les {targetLabelPlural} {subtypeLabels[type].toLowerCase()} ({subtypeCounts[type]})
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="apv2-filter-toggle-row">
                <button type="button" className="apv2-filter-toggle-btn" onClick={() => setFiltersOpen(o => !o)}>
                  {filtersOpen ? '▾' : '▸'} Filtrer
                  {activeFilterCount > 0 && <span className="apv2-filter-toggle-count">{activeFilterCount}</span>}
                </button>
              </div>

              {filtersOpen && (
                <div className="apv2-filter-panel">
                  <div className="apv2-filter-panel-row">
                    <span className="apv2-filter-panel-label">Section</span>
                    {sections.map(s => (
                      <button key={s.value} type="button"
                        className={`ceq-chip${sectionFilter.includes(s.value) ? ' ceq-chip--active' : ''}`}
                        onClick={() => toggleSectionFilter(s.value)}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                  {hasSubtypeGrouping && (
                    <div className="apv2-filter-panel-row">
                      <span className="apv2-filter-panel-label">Type</span>
                      {subtypeOrder.filter(type => subtypeCounts[type] > 0).map(type => (
                        <button key={type} type="button"
                          className={`ceq-chip${typeFilter.includes(type) ? ' ceq-chip--active' : ''}`}
                          onClick={() => toggleTypeFilter(type)}>
                          {subtypeLabels[type]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {poolEquipments.length === 0 ? (
                <p className="apv2-sim-hint">Tous les {targetLabelPlural} éligibles sont déjà assignés à un lot.</p>
              ) : (
                <div className="apv2-flat-table-wrapper">
                  <table className="apv2-flat-table">
                    <thead>
                      <tr>
                        <th className="apv2-flat-table-th-check">
                          <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisible} aria-label="Tout sélectionner" />
                        </th>
                        <th>Note</th>
                        {hasSubtypeGrouping && <th>Type</th>}
                        <th>Section</th>
                        <th>Salle</th>
                        <th>Débit actuel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleEquipments.map(equipment => {
                        const isSelected = draftEquipmentIds.has(equipment._id);
                        const subtype = getSubtype(equipment);
                        const isNotRecommended = hasSubtypeGrouping && draftModel?.compatibleFaucetTypes
                          && !draftModel.compatibleFaucetTypes.includes(subtype);
                        return (
                          <tr
                            key={equipment._id}
                            className={isSelected ? 'apv2-flat-table-row--selected' : ''}
                            onClick={() => toggleRow(equipment._id)}
                          >
                            <td className="apv2-flat-table-td-check">
                              <input type="checkbox" checked={isSelected} onChange={() => toggleRow(equipment._id)} onClick={e => e.stopPropagation()} />
                            </td>
                            <td><RatingBadge letter={equipment.rating_letter} /></td>
                            {hasSubtypeGrouping && (
                              <td>
                                {subtypeLabels[subtype]}
                                {isNotRecommended && (
                                  <span className="apv2-model-mismatch-badge" title="Ce modèle n'est pas recommandé pour ce type de robinet, mais reste sélectionnable">
                                    Type non recommandé
                                  </span>
                                )}
                              </td>
                            )}
                            <td>{equipment.location?.name_section || '—'}</td>
                            <td>{equipment.location?.name_room || '—'}</td>
                            <td>{equipment.water_value ?? '—'} {equipment.water_value_unit || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {showDraftTargetTooHighWarning && (
                <p className="apv2-sim-warning">
                  <span>⚠️</span>
                  <span>Le débit choisi est supérieur ou égal au débit actuel d'au moins un équipement sélectionné — aucune économie ne sera calculée pour celui-ci.</span>
                </p>
              )}
            </>
          )}

          <div className="apv2-lot-draft-actions">
            {editingLotId && (
              <button type="button" className="apv2-footer-btn-ghost" onClick={resetDraft}>
                Annuler la modification
              </button>
            )}
            <button type="button" className="apv2-footer-btn-cta" onClick={commitLot} disabled={!canCommitLot}>
              {editingLotId ? 'Enregistrer le lot' : 'Ajouter ce lot'}
            </button>
          </div>
        </div>
      </div>
      <div className="apv2-step-footer">
        <span />
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );

  const renderReboundStep = () => (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">{moduleEyebrow}</span>
          <h2 className="apv2-step-heading">Taux de rebond</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {STEP_LABELS.length}</span>
      </div>
      <div className="apv2-step-body">
        <div className="apv2-step-explanation">
          <span className="apv2-step-explanation-icon">💡</span>
          <p className="apv2-step-explanation-text">
            Quand le débit d'un point d'eau est réduit, les usagers peuvent compenser en l'utilisant plus longtemps.
            Ce taux réduit l'économie théorique calculée à partir du seul débit. Il s'applique à l'ensemble des lots.
          </p>
        </div>
        <div className="apv2-rebound-row">
          <div className="cus-range-wrapper">
            <input
              type="range"
              className="cus-range-input"
              min={0}
              max={100}
              step={1}
              value={Math.round(reboundRate * 100)}
              onChange={e => setReboundRate(Number(e.target.value) / 100)}
            />
            <div className="cus-range-markers">
              <span>0 %</span><span>50 %</span><span>100 %</span>
            </div>
          </div>
          <span className="apv2-rebound-value">{Math.round(reboundRate * 100)} %</span>
        </div>
        <p className="apv2-rebound-recommended-hint">
          Valeur recommandée : {Math.round(DEFAULT_REBOUND_RATE * 100)} %
          {reboundRate !== DEFAULT_REBOUND_RATE && (
            <button type="button" className="apv2-reset-link" onClick={() => setReboundRate(DEFAULT_REBOUND_RATE)}>
              Réinitialiser à la valeur recommandée
            </button>
          )}
        </p>
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );

  const renderResultsStep = () => (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">{moduleEyebrow}</span>
          <h2 className="apv2-step-heading">Résultats</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {STEP_LABELS.length}</span>
      </div>
      <div className="apv2-step-body">
      {!simulationResult.ready ? (
        <p className="apv2-sim-hint">Complétez les étapes précédentes pour afficher les résultats détaillés.</p>
      ) : (
        <>
          <div className="apv2-summary-cards">
            <div className="apv2-summary-card">
              <span className="apv2-summary-value">{simulationResult.totalSavingsM3.toFixed(1)} m³</span>
              <span className="apv2-summary-label">économisés / an</span>
            </div>
            <div className="apv2-summary-card">
              <span className="apv2-summary-value">
                {simulationResult.waterPriceAvailable
                  ? `${simulationResult.savingsEurTotalPerYear.toFixed(0)} €`
                  : 'N/A'}
              </span>
              <span className="apv2-summary-label">
                {simulationResult.waterPriceAvailable ? 'économisés / an' : 'prix de l\'eau non renseigné'}
              </span>
              {simulationResult.waterPriceAvailable && (
                <div className="apv2-summary-breakdown">
                  <span>dont {simulationResult.savingsEurWaterPerYear.toFixed(0)} € eau</span>
                  {simulationResult.heatingPriceAvailable ? (
                    <span>dont {simulationResult.savingsEurHeatingPerYear.toFixed(0)} € chauffage</span>
                  ) : (
                    <span className="apv2-summary-breakdown-warning">Coût du chauffage non calculable — prix de l'énergie non renseigné</span>
                  )}
                </div>
              )}
            </div>
            <div className="apv2-summary-card">
              <span className="apv2-summary-value">{simulationResult.savingsPct.toFixed(0)} %</span>
              <span className="apv2-summary-label">de réduction</span>
            </div>
            <div className="apv2-summary-card apv2-summary-card--secondary">
              <span className="apv2-summary-value">{simulationResult.totalCurrentM3.toFixed(1)} m³</span>
              <span className="apv2-summary-label">consommation actuelle sélectionnée</span>
            </div>
            <div className="apv2-summary-card apv2-summary-card--secondary">
              <span className="apv2-summary-value">
                {simulationResult.investmentFullyKnown ? `${simulationResult.totalInvestmentEur.toFixed(0)} €` : 'N/A'}
              </span>
              <span className="apv2-summary-label">
                {simulationResult.investmentFullyKnown ? 'investissement total' : 'coût inconnu pour au moins un lot (débit manuel)'}
              </span>
            </div>
            <div className="apv2-summary-card apv2-summary-card--secondary">
              <span className="apv2-summary-value">
                {simulationResult.paybackYears != null ? formatPayback(simulationResult.paybackYears) : 'N/A'}
              </span>
              <span className="apv2-summary-label">retour sur investissement</span>
            </div>
          </div>

          <div className="apv2-results-table-wrapper apv2-lot-summary-table-wrapper">
            <table className="apv2-results-table apv2-lot-summary-table">
              <thead>
                <tr>
                  <th>Lot</th>
                  <th className="apv2-th-right">Équipements</th>
                  <th className="apv2-th-right">Économie d'eau</th>
                  <th className="apv2-th-right">Réduction</th>
                  <th className="apv2-th-right">Investissement</th>
                </tr>
              </thead>
              <tbody>
                {lotBreakdown.map(({ lot, count, totalSavingsM3, savingsPct, investmentEur }) => (
                  <tr key={lot.id}>
                    <td>
                      {getLotLabel(lot)}
                      <div className="apv2-results-sublabel">{getLotFlow(lot)} L/min</div>
                    </td>
                    <td className="apv2-th-right">{count}</td>
                    <td className="apv2-th-right">{totalSavingsM3.toFixed(1)} m³/an</td>
                    <td className="apv2-th-right">{savingsPct.toFixed(0)} %</td>
                    <td className="apv2-th-right">{investmentEur != null ? `${investmentEur.toFixed(0)} €` : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="apv2-results-table-wrapper">
            <table className="apv2-results-table">
              <thead>
                <tr>
                  <th>Point d'eau</th>
                  <th>Débit</th>
                  <th>Consommation (m³/an)</th>
                  <th className="apv2-th-right">Économie</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Bar width is proportional to sqrt(current consumption), not the raw value:
                  // it compresses extreme gaps between a big and a small consumer while keeping
                  // ordering faithful, and a floor keeps the smallest bars legible.
                  const MIN_BAR_PCT = 18;
                  const MAX_BAR_PCT = 100;
                  const maxSqrtM3 = Math.max(1e-9, ...simulationResult.perEquipment.map(r => Math.sqrt(r.currentM3)));

                  const sortedResults = [...simulationResult.perEquipment].sort((a, b) => b.currentM3 - a.currentM3);

                  return sortedResults.map(result => {
                    const equipment = simulatedEquipments.find(e => e._id === result.equipmentId);
                    const noGain = result.currentFlow != null && result.targetFlow >= result.currentFlow;
                    const hasConsumption = result.currentM3 > 0;

                    const totalBarPct = MIN_BAR_PCT + (Math.sqrt(result.currentM3) / maxSqrtM3) * (MAX_BAR_PCT - MIN_BAR_PCT);
                    const newShareOfTotal = hasConsumption ? result.newM3 / result.currentM3 : 0;
                    const filledShare = newShareOfTotal * 100;
                    const savedShare = 100 - filledShare;

                    return (
                      <tr key={result.equipmentId}>
                        <td>
                          <div className="apv2-results-location">{equipment?.location?.name_section || '—'}</div>
                          <div className="apv2-results-sublabel">{equipment?.location?.name_room || '—'}</div>
                        </td>
                        <td className="apv2-results-flow">
                          {result.currentFlow} <span className="apv2-results-arrow">→</span> {result.targetFlow}
                          <span className="apv2-results-unit"> {equipment?.water_value_unit}</span>
                        </td>
                        <td>
                          {hasConsumption ? (
                            <>
                              <div className="apv2-savings-bar-track" style={{ width: `${totalBarPct}%` }}>
                                <div className="apv2-savings-bar-fill" style={{ width: `${filledShare}%` }} />
                                {savedShare > 0 && (
                                  <div className="apv2-savings-bar-saved" style={{ width: `${savedShare}%` }} title={`${result.savingsM3.toFixed(1)} m³/an économisés`} />
                                )}
                              </div>
                              <div className="apv2-results-sublabel">
                                {result.currentM3.toFixed(1)} <span className="apv2-results-arrow">→</span> {result.newM3.toFixed(1)} m³
                              </div>
                            </>
                          ) : (
                            <div className="apv2-results-sublabel apv2-results-sublabel--muted">Pas de consommation significative</div>
                          )}
                        </td>
                        <td className="apv2-th-right">
                          {noGain ? (
                            <span className="apv2-no-gain-badge">Aucun gain</span>
                          ) : (
                            <>
                              <div className="apv2-results-savings-pct">-{result.savingsPct.toFixed(0)} %</div>
                              <div className="apv2-results-sublabel">-{result.savingsM3.toFixed(1)} m³/an</div>
                            </>
                          )}
                          {result.hasNonReducibleUsage && (
                            <span className="apv2-non-reducible-badge" title="Contient un usage à volume fixe, non affecté par le débit">
                              Volume fixe
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </>
      )}
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <span />
      </div>
    </div>
  );

  const STEP_RENDERERS = [renderCompositionStep, renderReboundStep, renderResultsStep];

  return (
    <div>
      <div className="apv2-wizard-shell">
        <h2 className="apv2-wizard-title">{moduleEyebrow}</h2>
        <div className="apv2-wizard-nav">
          <button type="button" className="apv2-wizard-nav-btn" onClick={goPrevious} disabled={currentStep === 0}>
            ← Précédent
          </button>
          <Stepper
            steps={STEP_LABELS}
            currentStep={currentStep}
            furthestStepReached={furthestStepReached}
            onStepClick={goToStep}
          />
          <button type="button" className="apv2-wizard-nav-btn" onClick={goNext} disabled={!canAdvance || currentStep === STEP_LABELS.length - 1}>
            Suivant →
          </button>
        </div>

        <div className={`apv2-persistent-summary ${!simulationResult.ready ? 'apv2-persistent-summary--empty' : ''}`}>
          {simulationResult.ready ? (
            <>
              <div className="apv2-persistent-summary-stat">
                <span className="apv2-persistent-summary-value">{simulationResult.totalSavingsM3.toFixed(1)} m³</span>
                <span className="apv2-persistent-summary-label">économisés / an</span>
              </div>
              <div className="apv2-persistent-summary-stat">
                <span className="apv2-persistent-summary-value">{simulationResult.waterPriceAvailable ? `${simulationResult.savingsEurTotalPerYear.toFixed(0)} €` : 'N/A'}</span>
                <span className="apv2-persistent-summary-label">économisés / an</span>
              </div>
              <div className="apv2-persistent-summary-stat">
                <span className="apv2-persistent-summary-value">{simulationResult.savingsPct.toFixed(0)} %</span>
                <span className="apv2-persistent-summary-label">de réduction</span>
              </div>
              <div className="apv2-persistent-summary-stat">
                <span className="apv2-persistent-summary-value">{simulationResult.paybackYears != null ? formatPayback(simulationResult.paybackYears) : 'N/A'}</span>
                <span className="apv2-persistent-summary-label">retour sur investissement</span>
              </div>
            </>
          ) : (
            <span className="apv2-persistent-summary-empty-text">Composez au moins un lot (modèle + équipements) pour voir l'estimation.</span>
          )}
        </div>
      </div>

      {STEP_RENDERERS[currentStep]()}
    </div>
  );
};

export default FlowReductionWizard;
